/**
 * 用户登录 + 命例云同步（ziwei-knowledge 服务）
 *
 * - 登录/注册：元紫自有账号（方案 C）POST <knowledge>/api/v1/auth/{login,register}
 *   body {email,password} / {username,email,password,confirm_password} → {code:0, data:{token, refresh_token, expires_in, user}}
 *   token 即 ziwei 自签 JWT（Subject=access），ziwei 后端 UserAuth 直接校验
 * - 客户端来源标识：所有请求带 X-Client（App=app / web=web），服务端 RequireApp 拦截 App 独占接口
 * - 命例：前缀 /ziwei-knowledge（默认，生产经网关/CLB）或 VITE_KNOWLEDGE_API 覆盖
 *   （本地 dev 可指向 http://localhost:8090；vite proxy 已转发 /ziwei-knowledge → :8090）
 *   GET/POST /api/v1/user/cases，PUT/DELETE /api/v1/user/cases/:id，均需 Authorization: Bearer <JWT>
 * - UserCase.input / meta 为 JSON 字符串，本模块负责与 CaseRecord 互转
 * - 登录态持久化：localStorage（key paipan.auth）
 */
import type { BirthInput } from '../types/api';
import type { Meta } from '../types/base';
import { chartInputKey, normalizeRecord } from '../engine/chart-store';
import type { CaseRecord } from '../engine/chart-store';
import { isNativeApp } from './screenGuard';

/** 命例云服务基础地址：默认同源 /ziwei-knowledge；可用 VITE_KNOWLEDGE_API 覆盖（本地 dev → http://localhost:8090） */
export const KNOWLEDGE_BASE =
  (import.meta.env.VITE_KNOWLEDGE_API as string | undefined)?.replace(/\/$/, '') || '/ziwei-knowledge';

/** ArmorClaw 登录服务基础地址（可用 VITE_AUTH_API 环境变量覆盖） */
const AUTH_API =
  (import.meta.env.VITE_AUTH_API as string | undefined)?.replace(/\/$/, '') || 'https://api.armorclaw.cn';

/** 统一响应信封（code=0 成功；与 ziwei 系列服务约定一致） */
interface Envelope<T> {
  code: number;
  data: T;
  /** armor 后端信封错误文案字段为 msg；message 兼容其它来源 */
  msg?: string;
  message?: string;
}

/** 登录用户信息（后端 user 字段可能更多，只声明所需） */
export interface AuthUser {
  email?: string;
  nickname?: string;
}

/** 登录响应 data */
export interface LoginResult {
  token: string;
  refresh_token?: string;
  expires_in?: number;
  user: AuthUser;
}

/** 云端命例（UserCase：input/meta 为 JSON 字符串） */
export interface UserCase {
  id: string;
  user_id: string;
  chart_id: string;
  input: string;
  meta: string;
  name: string;
  remark: string | null;
  category: string | null;
  sort: number;
  created_at: string;
  updated_at: string;
}

/** 登录态持久化结构（localStorage paipan.auth） */
export interface StoredAuth {
  token: string;
  user: AuthUser;
}

/** 取全局 localStorage（浏览器/WebView；node 测试等无 window 时返回 null） */
function getLocalStorage(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** 拼接命例云服务全路径（前缀 /ziwei-knowledge 或 VITE_KNOWLEDGE_API 覆盖） */
function knowledgeUrl(path: string): string {
  return KNOWLEDGE_BASE + path;
}

/** 客户端来源标识头（App=app / web=web）：服务端 RequireApp 据此拦截 App 独占接口 */
function clientHeaders(): Record<string, string> {
  return { 'X-Client': isNativeApp() ? 'app' : 'web' };
}

/** 请求封装：统一信封解析（code=0 成功），带可选 Bearer token + X-Client 来源头 */
async function request<T>(url: string, init: { method: string; token?: string; body?: unknown }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: init.method,
      headers: {
        'Content-Type': 'application/json',
        ...clientHeaders(),
        ...(init.token ? { Authorization: `Bearer ${init.token}` } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch (e) {
    throw new Error(`网络请求失败：${e instanceof Error ? e.message : String(e)}`);
  }
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    // 非 JSON 响应
  }
  const env = json as Envelope<T> | null;
  if (!env || typeof env.code !== 'number') {
    throw new Error(`响应格式非法（HTTP ${res.status}）`);
  }
  if (env.code !== 0) {
    throw new Error(env.msg || env.message || `接口错误（code ${env.code}）`);
  }
  return env.data;
}

/** 登录：返回 token / user（失败抛错） */
export async function login(email: string, password: string): Promise<LoginResult> {
  return request<LoginResult>(knowledgeUrl('/api/v1/auth/login'), {
    method: 'POST',
    body: { email, password },
  });
}

/** 注册：成功即登录（后端返回 token，形状同 login；username 必填 2-50） */
export async function register(
  username: string,
  email: string,
  password: string,
  confirm_password: string,
): Promise<LoginResult> {
  return request<LoginResult>(knowledgeUrl('/api/v1/auth/register'), {
    method: 'POST',
    body: { username, email, password, confirm_password },
  });
}

/** 发送找回密码验证码到邮箱（失败抛错） */
export async function sendResetCode(email: string): Promise<void> {
  await request<{ message: string }>(`${AUTH_API}/api/v1/auth/send-reset-code`, {
    method: 'POST',
    body: { email },
  });
}

/** 校验邮箱 + 验证码，换取一次性重置 token（失败抛错） */
export async function verifyResetCode(email: string, code: string): Promise<string> {
  const data = await request<{ reset_token: string }>(`${AUTH_API}/api/v1/auth/verify-reset-code`, {
    method: 'POST',
    body: { email, code },
  });
  return data.reset_token;
}

/** 用 reset_token 设置新密码（失败抛错） */
export async function resetPassword(
  reset_token: string,
  new_password: string,
  confirm_password: string,
): Promise<void> {
  await request<{ message: string }>(`${AUTH_API}/api/v1/auth/reset-password`, {
    method: 'POST',
    body: { reset_token, new_password, confirm_password },
  });
}

/**
 * 拉取云端全量命例：input/meta JSON 解析回 CaseRecord；
 * 同时返回 chartId → UserCase.id 映射（云增删改需要 id 定位记录）。
 * 损坏记录（JSON 解析失败）跳过。
 */
export async function fetchCases(token: string): Promise<{ records: CaseRecord[]; idMap: Record<string, string> }> {
  const data = await request<{ items: UserCase[] }>(knowledgeUrl('/api/v1/user/cases'), { method: 'GET', token });
  const records: CaseRecord[] = [];
  const idMap: Record<string, string> = {};
  for (const uc of data?.items ?? []) {
    let input: BirthInput | null = null;
    let meta: Meta | null = null;
    try {
      input = JSON.parse(uc.input) as BirthInput;
    } catch {
      // 跳过损坏记录
    }
    try {
      meta = JSON.parse(uc.meta) as Meta;
    } catch {
      // 跳过损坏记录
    }
    if (!input || !meta) continue;
    const createdAt = Number(new Date(uc.created_at)) || Date.now();
    const record = normalizeRecord({
      chartId: uc.chart_id,
      input,
      meta,
      createdAt,
      name: uc.name ?? undefined,
      remark: uc.remark?.trim() || undefined,
      category: uc.category?.trim() || undefined,
    });
    records.push(record);
    idMap[record.chartId] = uc.id;
  }
  return { records, idMap };
}

/** CaseRecord → 云端建/改 body（input/meta 序列化为 JSON 字符串） */
function caseBody(rec: CaseRecord) {
  return {
    chart_id: rec.chartId,
    input: JSON.stringify(rec.input),
    meta: JSON.stringify(rec.meta),
    name: rec.name,
    remark: rec.remark ?? '',
    category: rec.category ?? '',
    sort: 0,
  };
}

/** 新建云端命例 → 返回 UserCase（含 id） */
export async function createCase(token: string, rec: CaseRecord): Promise<UserCase> {
  return request<UserCase>(knowledgeUrl('/api/v1/user/cases'), { method: 'POST', token, body: caseBody(rec) });
}

/** 更新云端命例（id = UserCase.id） */
export async function updateCase(token: string, id: string, rec: CaseRecord): Promise<UserCase> {
  return request<UserCase>(knowledgeUrl(`/api/v1/user/cases/${id}`), { method: 'PUT', token, body: caseBody(rec) });
}

/** 删除云端命例（id = UserCase.id） */
export async function deleteCase(token: string, id: string): Promise<void> {
  await request<unknown>(knowledgeUrl(`/api/v1/user/cases/${id}`), { method: 'DELETE', token });
}

/** 拉取云端分类列表（无/失败由调用方兜底；返回去空字符串数组） */
export async function fetchCategories(token: string): Promise<string[]> {
  const data = await request<{ items: string[] | null }>(knowledgeUrl('/api/v1/user/case-categories'), {
    method: 'GET',
    token,
  });
  return (data?.items ?? []).filter((c) => typeof c === 'string' && c.trim());
}

/** 覆盖写云端分类列表 */
export async function putCategories(token: string, list: string[]): Promise<void> {
  await request<unknown>(knowledgeUrl('/api/v1/user/case-categories'), { method: 'PUT', token, body: { items: list } });
}

// ---- 登录态持久化（localStorage） ----

const AUTH_KEY = 'paipan.auth';

/** 读取本地登录态（无/损坏返回 null） */
export function loadStoredAuth(): StoredAuth | null {
  try {
    const store = getLocalStorage();
    if (!store) return null;
    const raw = store.getItem(AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAuth;
    if (parsed && typeof parsed.token === 'string' && parsed.token) return parsed;
    return null;
  } catch {
    return null;
  }
}

/** 保存登录态 */
export function storeAuth(token: string, user: AuthUser): void {
  try {
    const store = getLocalStorage();
    if (!store) return;
    store.setItem(AUTH_KEY, JSON.stringify({ token, user }));
  } catch {
    // 静默失败（隐私模式等）
  }
}

/** 清除登录态 */
export function clearStoredAuth(): void {
  try {
    const store = getLocalStorage();
    if (!store) return;
    store.removeItem(AUTH_KEY);
  } catch {
    // 静默失败
  }
}

/** 云端+本地合并结果 */
export interface CloudMergeResult {
  /** 云端为准合并后的完整列表（含本地新增；已剔除他端删除的本地残留） */
  merged: CaseRecord[];
  /** chartInputKey → 云端 UserCase.id（云端已知部分；本地新增的 id 由调用方上传后补） */
  ledger: Record<string, string>;
  /** 仅本地存在、需逐条上传的命例（账本中无云 id 的真新增） */
  localOnly: CaseRecord[];
}

/**
 * 合并策略（云端为准 + 账本区分「本地新增」与「他端已删」，实现删除传播）：
 * - 以 chartInputKey(input) 为稳定身份；相同 input 云端优先
 * - 本地记录云端已无：
 *   · 账本 knownLedger 里有其云 id → 判定为他端删除 → 本地丢弃（不再重传，删除得以传播）
 *   · 账本里也没有 → 本机真正新增 → 保留并加入 localOnly 待上传
 * - hideBirth 为「仅本地」展示偏好（不随 caseBody 上传）；「云端为准」会用无该字段的云端记录
 *   覆盖本地，故合并时把本地 hideBirth 回填到同 input 的云端记录，避免同步后「隐藏生辰」开关丢失
 * - merged 按 createdAt 降序（新在前）
 */
export function mergeCloudAndLocal(
  local: CaseRecord[],
  cloud: CaseRecord[],
  cloudIds: Record<string, string>,
  knownLedger: Record<string, string>,
): CloudMergeResult {
  const cloudByKey = new Map<string, CaseRecord>();
  const ledger: Record<string, string> = {};
  for (const c of cloud) {
    const k = chartInputKey(c.input);
    cloudByKey.set(k, c);
    const id = cloudIds[c.chartId] ?? '';
    if (id) ledger[k] = id;
  }
  // hideBirth 为「仅本地」展示偏好，不参与云同步；「云端为准」会用无该字段的云端记录覆盖本地，
  // 故先从本地收集本机开启的 hideBirth，合并时回填到同 input 的云端记录，避免同步后开关丢失。
  const localHideByKey = new Map<string, boolean>();
  for (const r of local) if (r.hideBirth) localHideByKey.set(chartInputKey(r.input), true);

  const merged: CaseRecord[] = [...cloudByKey.entries()].map(([k, c]) =>
    localHideByKey.get(k) ? { ...c, hideBirth: true } : c,
  );
  const localOnly: CaseRecord[] = [];
  for (const r of local) {
    const k = chartInputKey(r.input);
    if (cloudByKey.has(k)) continue; // 云端已有 → 云端优先，跳过本地
    if (knownLedger[k]) continue; // 账本有云 id 但云端已无 → 他端已删 → 本地丢弃
    merged.push(r);
    localOnly.push(r);
  }
  merged.sort((a, b) => b.createdAt - a.createdAt);
  return { merged, ledger, localOnly };
}
